import React, { useState } from 'react';
import { KeyRound, Mail, LogIn, AlertCircle, Eye, EyeOff } from 'lucide-react';
import Button from '../../components/Button';
import Card from '../../components/Card';
import OtpVerification from './OtpVerification';
import { useAuth } from '../../context/AuthContext';
import { UI_TEXT } from '../../config/ui-text';

interface LoginProps {
  onSwitch: () => void;
  onSuccess: (email: string) => void;
}

const Login: React.FC<LoginProps> = ({ onSwitch, onSuccess }) => {
  const { login, verifyOtp } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showOtpVerification, setShowOtpVerification] = useState(false);
  const [unverifiedEmail, setUnverifiedEmail] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);
    
    try {
      const result = await login(email, password);
      // A marketplace account signing in here is signed in fine (shared session) but
      // belongs on the marketplace. A full page load, not a router navigate: the
      // marketplace has its own auth context, which reads the session on mount.
      if (result.redirect) {
        window.location.href = result.redirect;
        return;
      }
      if (result.success) {
        onSuccess(email);
      } else {
        // Check if this requires OTP verification
        if (result.requiresVerification) {
          setUnverifiedEmail(email);
          setShowOtpVerification(true);
        } else {
          setError(result.message);
        }
      }
    } catch (err) {
      setError('An unexpected error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOtpVerify = async (otpCode: string) => {
    setError(null);
    setIsLoading(true);
    
    try {
      const result = await verifyOtp(unverifiedEmail!, otpCode);
      if (result.success) {
        onSuccess(unverifiedEmail!);
      } else {
        setError(result.message);
      }
    } catch (err) {
      setError('An unexpected error occurred during verification');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendOtp = async () => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL || ''}/resend-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: unverifiedEmail }),
        credentials: 'include'
      });

      if (!response.ok) {
        const data = await response.json();
        const detailMsg = typeof data.detail === 'string' ? data.detail : data.detail?.message;
        setError(data.error || detailMsg || 'Failed to resend verification code');
      }
    } catch (err) {
      setError('Failed to resend verification code');
    }
  };

  if (showOtpVerification && unverifiedEmail) {
    return (
      <OtpVerification
        email={unverifiedEmail}
        onVerify={handleOtpVerify}
        onResend={handleResendOtp}
        onBack={() => {
          setShowOtpVerification(false);
          setUnverifiedEmail(null);
          setError(null);
        }}
        isLoading={isLoading}
        error={error}
      />
    );
  }

  return (
    <Card className="w-full max-w-md mx-auto p-8">
      <div className="text-center mb-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Welcome Back</h1>
        <p className="text-gray-600">Sign in to your account to continue</p>
      </div>
      
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg flex items-start">
          <AlertCircle className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5" />
          <span>{typeof error === 'string' ? error : 'Something went wrong. Please try again.'}</span>
        </div>
      )}
      
      <form onSubmit={handleSubmit}>
        <div className="space-y-6">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
              Email
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Mail className="h-5 w-5 text-gray-400" />
              </div>
              <input
                id="email"
                name="email"
                type="email"
                required
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder="you@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
          </div>
          
          <div>
            <div className="flex items-center justify-between mb-1">
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                Password
              </label>
              <a
                href="/reset-password"
                className="text-sm font-medium text-[#07E5D1] hover:text-[#06D4C2] transition-colors"
              >
                Forgot password?
              </a>
            </div>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <KeyRound className="h-5 w-5 text-gray-400" />
              </div>
              <input
                id="password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                required
                className="block w-full pl-10 pr-10 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? UI_TEXT.BUTTONS.HIDE_PASSWORD : UI_TEXT.BUTTONS.SHOW_PASSWORD}
                aria-pressed={showPassword}
              >
                {showPassword ? (
                  <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                ) : (
                  <Eye className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                )}
              </button>
            </div>
          </div>
          
          <Button 
            type="submit" 
            fullWidth 
            disabled={isLoading}
            icon={isLoading ? (
              <span className="inline-block h-5 w-5 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
            ) : (
              <LogIn className="h-5 w-5" />
            )}
          >
            {isLoading ? 'Signing in...' : 'Sign in'}
          </Button>
        </div>
      </form>
      
      <div className="mt-8 text-center">
        <p className="text-sm text-gray-600">
          Don't have an account?{' '}
          <button 
            onClick={onSwitch} 
            className="font-medium text-blue-600 hover:text-blue-500"
          >
            Book a Meeting
          </button>
        </p>
      </div>
    </Card>
  );
};

export default Login;